import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  Business,
} from '@mui/icons-material';
import { stateAssociationAPI } from '../api/stateAssociation.api';
import { useAuth } from '../context/AuthContext';

/**
 * Layout and styling - Matching Athlete Design Theme
 */
const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem 0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(41, 30, 108, 0.15)',
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  maxWidth: '480px',
  width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: '#291e6c' },
    '&.Mui-focused fieldset': { borderColor: '#291e6c' },
  },
}));

const StyledButton = styled(Button)({
  padding: '14px 32px',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
  backgroundColor: '#291e6c',
  color: 'white',
  '&:hover': { backgroundColor: '#1f1750' },
});

const StateAssociationLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  // Login form state
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Forgot Password dialog state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOTP, setForgotOTP] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  /**
   * Login form handlers
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (
      !formData.email ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = 'Valid email is required';
    }
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await stateAssociationAPI.login(formData);
      if (response.success || response.data) {
        login(
          response.data,
          response.data.token || response.token,
          'stateAssociation'
        );
        toast.success('Login successful!');
        setTimeout(() => {
          navigate('/state-association/dashboard');
        }, 1000);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(
        error.response?.data?.message ||
          'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Forgot Password handlers
   */
  const resetForgotState = () => {
    setForgotStep(1);
    setForgotEmail('');
    setForgotOTP('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotError('');
    setForgotLoading(false);
  };

  const handleForgotOpen = () => {
    resetForgotState();
    setForgotOpen(true);
  };

  const handleForgotClose = () => {
    setForgotOpen(false);
  };

  // Step 1: send OTP
  const handleSendOTP = async () => {
    if (
      !forgotEmail ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)
    ) {
      setForgotError('Valid email is required');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    try {
      await stateAssociationAPI.forgotPassword({ email: forgotEmail });
      toast.success('Password reset OTP sent to your email');
      setForgotStep(2);
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotError(
        error.response?.data?.message || 'Failed to send reset instructions.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 2: verify OTP and get resetToken
  const handleVerifyOTP = async () => {
    if (!forgotOTP || forgotOTP.length < 4) {
      setForgotError('Please enter the OTP sent to your email');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    try {
      const response = await stateAssociationAPI.verifyPasswordResetOTP({
        email: forgotEmail,
        otp: forgotOTP,
      });
      if (response.success && response.data?.resetToken) {
        setResetToken(response.data.resetToken);
        toast.success('OTP verified successfully');
        setForgotStep(3);
      } else if (response.resetToken) {
        setResetToken(response.resetToken);
        toast.success('OTP verified successfully');
        setForgotStep(3);
      } else {
        setForgotError('Invalid response from server. Please try again.');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setForgotError(
        error.response?.data?.message || 'Failed to verify OTP.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 3: reset password using resetToken
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setForgotError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setForgotError('Password and confirm password must match');
      return;
    }
    if (!resetToken) {
      setForgotError('Missing reset token. Please restart the process.');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    try {
      const response = await stateAssociationAPI.resetPassword({
        email: forgotEmail,
        resetToken,
        newPassword,
      });
      if (response.success) {
        toast.success('Password reset successfully. Please login with your new password.');
        setForgotOpen(false);
        resetForgotState();
      } else {
        setForgotError(
          response.message || 'Failed to reset password. Please try again.'
        );
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setForgotError(
        error.response?.data?.message || 'Failed to reset password.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // Decide which action to run based on the current step
  const handleForgotPrimaryAction = () => {
    if (forgotStep === 1) return handleSendOTP();
    if (forgotStep === 2) return handleVerifyOTP();
    if (forgotStep === 3) return handleResetPassword();
  };

  const primaryButtonLabel =
    forgotStep === 1
      ? 'Send OTP'
      : forgotStep === 2
      ? 'Verify OTP'
      : 'Reset Password';

  return (
    <PageWrapper>
      <Container maxWidth="sm">
        <StyledPaper elevation={3}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Business sx={{ fontSize: 48, color: '#291e6c', mb: 2 }} />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#291e6c',
                fontFamily: "'Oswald', sans-serif",
                mb: 1,
              }}
            >
              STATE ASSOCIATION LOGIN
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to access your dashboard
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <StyledTextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#291e6c' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <StyledTextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#291e6c' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link
                component="button"
                type="button"
                onClick={handleForgotOpen}
                sx={{
                  fontSize: '0.9rem',
                  color: '#291e6c',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            <StyledButton
              fullWidth
              type="submit"
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  <LoginIcon />
                )
              }
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </StyledButton>
          </form>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Alert severity="info" sx={{ mt: 2 }}>
            Use your registered email and password to login
          </Alert>
        </StyledPaper>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotOpen}
        onClose={handleForgotClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(41, 30, 108, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: '#291e6c',
            fontFamily: "'Oswald', sans-serif",
          }}
        >
          Forgot Password
        </DialogTitle>
        <DialogContent>
          {forgotStep === 1 && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Enter your registered email address to receive an OTP for password reset.
              </Typography>
              <StyledTextField
                fullWidth
                label="Email"
                type="email"
                value={forgotEmail}
                onChange={(e) => {
                  setForgotEmail(e.target.value);
                  if (forgotError) setForgotError('');
                }}
                error={!!forgotError}
                helperText={forgotError}
              />
            </>
          )}

          {forgotStep === 2 && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                An OTP has been sent to {forgotEmail}. Enter it below to verify.
              </Typography>
              <StyledTextField
                fullWidth
                label="OTP"
                value={forgotOTP}
                onChange={(e) => {
                  setForgotOTP(e.target.value);
                  if (forgotError) setForgotError('');
                }}
                error={!!forgotError}
                helperText={forgotError}
              />
            </>
          )}

          {forgotStep === 3 && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Enter your new password below.
              </Typography>
              <StyledTextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (forgotError) setForgotError('');
                }}
                sx={{ mb: 2 }}
              />
              <StyledTextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (forgotError) setForgotError('');
                }}
                error={!!forgotError}
                helperText={forgotError}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleForgotClose}
            sx={{ color: '#291e6c', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleForgotPrimaryAction}
            disabled={forgotLoading}
            variant="contained"
            sx={{
              backgroundColor: '#291e6c',
              '&:hover': { backgroundColor: '#1f1750' },
              textTransform: 'none',
            }}
            startIcon={
              forgotLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null
            }
          >
            {primaryButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default StateAssociationLogin;